import { useState } from "react";
import { useQuery, useQueries, useMutation } from "@tanstack/react-query";
import { Plus, Trash2, Loader2, Users2 } from "lucide-react";
import { format } from "date-fns";
import type { Account, SmsfMember, Contact } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";

interface SmsfMembersPanelProps {
  account: Account;
}

export function SmsfMembersPanel({ account }: SmsfMembersPanelProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | undefined>();
  const [memberSince, setMemberSince] = useState<Date | undefined>();
  const [isActive, setIsActive] = useState(true);
  const [accumulationBalance, setAccumulationBalance] = useState<string>("");
  const [pensionBalance, setPensionBalance] = useState<string>("");
  const [concessionalContributions, setConcessionalContributions] = useState<string>("");
  const [nonConcessionalContributions, setNonConcessionalContributions] = useState<string>("");
  const [isInPensionPhase, setIsInPensionPhase] = useState(false);
  const [pensionStartDate, setPensionStartDate] = useState<Date | undefined>();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { toast } = useToast();

  // Fetch all contacts for dropdown
  const { data: contacts = [] } = useQuery<Contact[]>({
    queryKey: ["/api/crm/contacts"],
  });

  // Fetch SMSF members
  const { data: members = [], isLoading } = useQuery<SmsfMember[]>({
    queryKey: ["/api/crm/accounts", account.id, "smsf-members"],
    enabled: account.trustType === "smsf",
  });

  const retainedContacts = useQueries({
    queries: Array.from(new Set(members.map(member => member.contactId).filter((id): id is string => !!id)))
      .filter(id => !contacts.some(contact => contact.id === id))
      .map(id => ({ queryKey: ["/api/crm/contacts", id] })),
  });

  const addMutation = useMutation({
    mutationFn: async (data: {
      contactId: string;
      memberSince?: string;
      isActive?: number;
      accumulationBalance?: number;
      pensionBalance?: number;
      concessionalContributionsYtd?: number;
      nonConcessionalContributionsYtd?: number;
      isInPensionPhase?: number;
      pensionStartDate?: string;
    }) => {
      await apiRequest("POST", `/api/crm/accounts/${account.id}/smsf-members`, {
        smsfAccountId: account.id,
        ...data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "smsf-members"] });
      toast({ title: "Member added", description: "The SMSF member has been added successfully." });
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to add member", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/crm/accounts/${account.id}/smsf-members/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "smsf-members"] });
      toast({ title: "Member removed", description: "The SMSF member has been removed." });
      setDeleteId(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to remove member", variant: "destructive" });
    },
  });

  const handleCloseDialog = () => {
    setIsAddDialogOpen(false);
    setSelectedContactId(undefined);
    setMemberSince(undefined);
    setIsActive(true);
    setAccumulationBalance("");
    setPensionBalance("");
    setConcessionalContributions("");
    setNonConcessionalContributions("");
    setIsInPensionPhase(false);
    setPensionStartDate(undefined);
  };

  const handleAdd = () => {
    if (!selectedContactId) return;
    addMutation.mutate({
      contactId: selectedContactId,
      memberSince: memberSince ? format(memberSince, "yyyy-MM-dd") : undefined,
      isActive: isActive ? 1 : 0,
      accumulationBalance: accumulationBalance ? parseFloat(accumulationBalance) : undefined,
      pensionBalance: pensionBalance ? parseFloat(pensionBalance) : undefined,
      concessionalContributionsYtd: concessionalContributions ? parseFloat(concessionalContributions) : undefined,
      nonConcessionalContributionsYtd: nonConcessionalContributions ? parseFloat(nonConcessionalContributions) : undefined,
      isInPensionPhase: isInPensionPhase ? 1 : 0,
      pensionStartDate: pensionStartDate ? format(pensionStartDate, "yyyy-MM-dd") : undefined,
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: string | number | null) => {
    if (!amount || amount === "0") return "-";
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(typeof amount === "string" ? parseFloat(amount) : amount);
  };

  const getContactName = (contactId: string | null) => {
    if (!contactId) return "-";
    const contact = contacts.find(c => c.id === contactId)
      ?? retainedContacts.map(result => result.data as Contact | undefined).find(c => c?.id === contactId);
    if (!contact) return "-";
    return `${contact.firstName || ""} ${contact.lastName || ""}`.trim() || "-";
  };

  const getTotalBalance = (member: SmsfMember) => {
    const accum = parseFloat(member.accumulationBalance || "0");
    const pension = parseFloat(member.pensionBalance || "0");
    return accum + pension;
  };

  if (account.entityType !== "trust" || account.trustType !== "smsf") {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="smsf-members-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-muted-foreground">SMSF MEMBERS</h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsAddDialogOpen(true)}
          data-testid="button-add-smsf-member"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Member
        </Button>
      </div>

      {members.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center border rounded-md bg-white">
          <Users2 className="h-10 w-10 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No SMSF members yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Click "Add Member" to add a member to this SMSF
          </p>
        </div>
      ) : (
        <div className="border rounded-md bg-white">
          <Table>
            <TableHeader>
              <TableRow className="h-10 bg-muted/50">
                <TableHead className="text-xs font-medium px-2 py-1">Member Name</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Member Since</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Accumulation</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Pension</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Total Balance</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Pension Phase</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Status</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1 w-[60px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => (
                <TableRow
                  key={member.id}
                  className="h-10"
                  data-testid={`row-smsf-member-${member.id}`}
                >
                  <TableCell className="px-2 py-1 text-sm font-medium">
                    {getContactName(member.contactId)}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {formatDate(member.memberSince)}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {formatCurrency(member.accumulationBalance)}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {formatCurrency(member.pensionBalance)}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm font-medium">
                    {formatCurrency(getTotalBalance(member))}
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    <Badge
                      variant={member.isInPensionPhase === 1 ? "default" : "secondary"}
                      className="text-xs"
                    >
                      {member.isInPensionPhase === 1 ? "Yes" : "No"}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    <Badge
                      variant={member.isActive === 1 ? "default" : "secondary"}
                      className="text-xs"
                      data-testid={`badge-active-${member.id}`}
                    >
                      {member.isActive === 1 ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteId(member.id)}
                      data-testid={`button-delete-member-${member.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add SMSF Member</DialogTitle>
            <DialogDescription>
              Add a member to this SMSF with their balance and contribution details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Member <span className="text-destructive">*</span></Label>
              <Select value={selectedContactId} onValueChange={setSelectedContactId}>
                <SelectTrigger data-testid="select-contact">
                  <SelectValue placeholder="Select contact" />
                </SelectTrigger>
                <SelectContent>
                  {contacts.map((contact) => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {contact.firstName} {contact.lastName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Member Since</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !memberSince && "text-muted-foreground"
                      )}
                      data-testid="input-member-since"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {memberSince ? format(memberSince, "d MMM yyyy") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={memberSince}
                      onSelect={setMemberSince}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2 flex items-end">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isActive"
                    checked={isActive}
                    onCheckedChange={(checked) => setIsActive(checked === true)}
                    data-testid="checkbox-is-active"
                  />
                  <Label htmlFor="isActive" className="cursor-pointer">Active Member</Label>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Accumulation Balance ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={accumulationBalance}
                  onChange={(e) => setAccumulationBalance(e.target.value)}
                  placeholder="0.00"
                  data-testid="input-accumulation-balance"
                />
              </div>
              <div className="space-y-2">
                <Label>Pension Balance ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={pensionBalance}
                  onChange={(e) => setPensionBalance(e.target.value)}
                  placeholder="0.00"
                  data-testid="input-pension-balance"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Concessional Contributions YTD ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={concessionalContributions}
                  onChange={(e) => setConcessionalContributions(e.target.value)}
                  placeholder="0.00"
                  data-testid="input-concessional-contributions"
                />
              </div>
              <div className="space-y-2">
                <Label>Non-Concessional Contributions YTD ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={nonConcessionalContributions}
                  onChange={(e) => setNonConcessionalContributions(e.target.value)}
                  placeholder="0.00"
                  data-testid="input-non-concessional-contributions"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isInPensionPhase"
                    checked={isInPensionPhase}
                    onCheckedChange={(checked) => setIsInPensionPhase(checked === true)}
                    data-testid="checkbox-pension-phase"
                  />
                  <Label htmlFor="isInPensionPhase" className="cursor-pointer">In Pension Phase</Label>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Pension Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !pensionStartDate && "text-muted-foreground"
                      )}
                      disabled={!isInPensionPhase}
                      data-testid="input-pension-start-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {pensionStartDate ? format(pensionStartDate, "d MMM yyyy") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={pensionStartDate}
                      onSelect={setPensionStartDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={!selectedContactId || addMutation.isPending}
              data-testid="button-confirm-add-member"
            >
              {addMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove SMSF Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this SMSF member? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete-member"
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
